import { ColumnDef } from '@tanstack/react-table';
import { Pencil, Trash2 } from 'lucide-react';
import { DataTableColumnHeader } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { Project } from '../../types/project.types';

interface CreateProjectTableColumnsProps {
  t: (key: string) => string;
  onEdit: (project: Project) => void;
  onDelete: (project: Project) => void;
}

const namesCell = (names?: string) => {
  if (!names) return <span>-</span>;
  const list = names
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);
  if (list.length === 0) return <span>-</span>;
  const [first, ...rest] = list;
  return (
    <span className="truncate" title={names}>
      {first}
      {rest.length > 0 ? ` +${rest.length}` : ''}
    </span>
  );
};

export const createProjectTableColumns = ({
  t,
  onEdit,
  onDelete,
}: CreateProjectTableColumnsProps): ColumnDef<Project>[] => [
  {
    id: 'ProjectName',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('PROJECT_NAME')} />,
    accessorFn: (row) => row.ProjectName ?? '',
    cell: ({ row }) => <span className="truncate font-medium">{row.original.ProjectName}</span>,
  },
  {
    id: 'LeadNames',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('LEADS')} />,
    accessorFn: (row) => row.LeadNames ?? '',
    cell: ({ row }) => namesCell(row.original.LeadNames),
  },
  {
    id: 'MemberNames',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('MEMBERS')} />,
    accessorFn: (row) => row.MemberNames ?? '',
    cell: ({ row }) => namesCell(row.original.MemberNames),
  },
  {
    id: 'actions',
    // Card: top right, as the old portal's card header.
    meta: { mobile: 'badge' as const },
    header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => (
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onEdit(row.original)}
          aria-label={t('EDIT')}
        >
          <Pencil className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(row.original)}
          aria-label={t('DELETE')}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>
    ),
  },
];
